/**
 * envpulse AI — card de insight do ativo no EquipmentDetail.
 *
 * Mostra:
 *   - Health Score em ring colorido (lado esquerdo)
 *   - Lista de findings com severity colors (lado direito)
 *   - Breakdown por dimensão (temperatura/consumo/porta)
 *   - "Atualizado há Xh" + algo_version
 *
 * Recolhido por default (preferência do usuário em localStorage): a
 * análise é leitura de apoio, não o motivo de abrir a tela do ativo.
 * A faixa fechada carrega score e severidade pra que fechar não vire
 * esconder.
 *
 * Fontes: useEquipmentInsight() (TanStack Query, cache 5min frontend +
 * Redis 5min backend; worker materializa a cada 6h).
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Droplets,
  ChevronDown,
  Info,
  Lightbulb,
  Plug,
  RefreshCw,
  Sparkles,
  Thermometer,
  Zap,
} from "lucide-react";
import clsx from "clsx";

import { useEquipmentInsight } from "@/hooks/queries";
import { timeAgo } from "@/lib/format";
import type { EquipmentInsight, HealthGrade, InsightSeverity } from "@/types";

import { Card, CardBody } from "./Card";

// ----------------------------------------------------------------------------
// Cores e helpers
// ----------------------------------------------------------------------------

const GRADE_COLOR: Record<HealthGrade, { ring: string; text: string; label: string }> = {
  excellent: { ring: "text-emerald-500", text: "text-emerald-600 dark:text-emerald-400", label: "Excelente" },
  good:      { ring: "text-cyan-500",    text: "text-cyan-600 dark:text-cyan-400",       label: "Bom" },
  fair:      { ring: "text-amber-500",   text: "text-amber-600 dark:text-amber-400",     label: "Atenção" },
  poor:      { ring: "text-red-500",     text: "text-red-600 dark:text-red-400",         label: "Crítico" },
};

const SEVERITY_STYLE: Record<InsightSeverity, { icon: typeof Info; bg: string; ring: string; iconClr: string }> = {
  info: {
    icon: Info,
    bg: "bg-cyan-50 dark:bg-cyan-950/30",
    ring: "ring-cyan-200/60 dark:ring-cyan-800/40",
    iconClr: "text-cyan-600 dark:text-cyan-400",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50 dark:bg-amber-950/30",
    ring: "ring-amber-200/60 dark:ring-amber-800/40",
    iconClr: "text-amber-600 dark:text-amber-400",
  },
  critical: {
    icon: AlertCircle,
    bg: "bg-red-50 dark:bg-red-950/30",
    ring: "ring-red-200/60 dark:ring-red-800/40",
    iconClr: "text-red-600 dark:text-red-400",
  },
};

// Mapeia type de finding pra ícone temático
function findingDimensionIcon(type: string) {
  if (type === "compressor_desarmes" || type === "compressor_threshold_sugerido") return Plug;
  if (type.startsWith("compressor")) return Zap;
  if (type.startsWith("consumption")) return Zap;
  if (type.startsWith("sli") || type.startsWith("temp_")) return Thermometer;
  if (type.startsWith("door")) return Droplets;  // door = Droplets é meio random; mas evita reusar door icon vs porta
  return Sparkles;
}

// ----------------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------------

/**
 * Estado de aberto/fechado por usuário, não por unidade: a preferência é
 * sobre quanto ruído a pessoa quer na tela, e isso não muda de geladeira
 * pra geladeira.
 */
const CHAVE_ABERTO = "envpulse:insight-aberto";

function lerPreferencia(): boolean | null {
  try {
    const v = localStorage.getItem(CHAVE_ABERTO);
    return v === null ? null : v === "1";
  } catch {
    return null;   // navegador com storage bloqueado — cai no default
  }
}

function gravarPreferencia(aberto: boolean) {
  try {
    localStorage.setItem(CHAVE_ABERTO, aberto ? "1" : "0");
  } catch {
    // sem storage: a escolha vale só nesta sessão, e tudo bem
  }
}

interface Resumo {
  score: number;
  grade: HealthGrade;
  criticos: number;
  atencoes: number;
  computado_em: string;
}

function resumir(data: EquipmentInsight | undefined): Resumo | null {
  if (!data) return null;
  const findings = data.findings.filter(ehFinding);
  return {
    score: data.health_score,
    grade: data.health_grade,
    criticos: findings.filter((f) => f.severity === "critical").length,
    atencoes: findings.filter((f) => f.severity === "warning").length,
    computado_em: data.computed_at,
  };
}

// ----------------------------------------------------------------------------
// Componente principal
// ----------------------------------------------------------------------------

export function EquipmentAIInsightCard({ equipmentId }: { equipmentId: string | undefined }) {
  const query = useEquipmentInsight(equipmentId);
  const resumo = useMemo(() => resumir(query.data), [query.data]);

  const [aberto, setAberto] = useState(() => lerPreferencia() ?? false);

  /**
   * Recolhido por default resolve a poluição visual, mas não pode virar
   * esconderijo: um finding crítico atrás de um clique é informação que
   * chega tarde. Então a régua é dupla — a faixa fechada CARREGA a
   * severidade (contadores + barra colorida na borda), e crítico ainda
   * abre sozinho pra quem nunca mexeu no controle. Quem já escolheu
   * fechar mandou fechar, e a escolha dele vale.
   */
  useEffect(() => {
    if (resumo && resumo.criticos > 0 && lerPreferencia() === null) setAberto(true);
  }, [resumo]);

  function alternar() {
    setAberto((v) => {
      gravarPreferencia(!v);
      return !v;
    });
  }

  const severidade = resumo
    ? resumo.criticos > 0
      ? "critical"
      : resumo.atencoes > 0
        ? "warning"
        : "ok"
    : "ok";

  const BORDA = {
    critical: "before:bg-red-500",
    warning: "before:bg-amber-400",
    ok: "before:bg-gradient-to-b before:from-violet-500 before:to-cyan-500",
  }[severidade];

  return (
    <Card
      className={clsx(
        "mt-6 overflow-hidden relative",
        // Fita vertical na borda esquerda: a cor diz o estado sem ocupar
        // altura nenhuma, que é o ponto do card recolhido.
        "before:absolute before:inset-y-0 before:left-0 before:w-1 before:content-['']",
        BORDA,
      )}
    >
      <button
        type="button"
        onClick={alternar}
        aria-expanded={aberto}
        aria-controls="envpulse-ai-body"
        className={clsx(
          "flex w-full items-center gap-3 px-5 py-3 text-left transition-colors",
          "hover:bg-violet-50/40 dark:hover:bg-violet-950/20",
          aberto && "border-b border-surface-100 dark:border-slate-800",
        )}
      >
        <span className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-cyan-500 text-white shadow-sm">
          <Sparkles className="h-4 w-4" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">
            envpulse <span className="text-violet-600 dark:text-violet-400">AI</span>
            <span className="font-normal text-slate-400 dark:text-slate-500"> · Insights</span>
          </span>
          <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
            <FaixaResumo query={query} resumo={resumo} />
          </span>
        </span>

        {resumo && <SeloScore resumo={resumo} />}

        <ChevronDown
          className={clsx(
            "h-4 w-4 flex-none text-slate-400 transition-transform",
            aberto && "rotate-180",
          )}
        />
      </button>

      {aberto && (
        <CardBody id="envpulse-ai-body">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {query.data ? (
                <>
                  Atualizado {timeAgo(query.data.computed_at)} ·{" "}
                  <span className="font-mono">{query.data.algo_version}</span>
                </>
              ) : null}
            </span>
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              className="inline-flex items-center gap-1.5 rounded-md border border-surface-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-surface-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
              aria-label="Atualizar insight"
            >
              <RefreshCw className={clsx("h-3.5 w-3.5", query.isFetching && "animate-spin")} />
              Atualizar
            </button>
          </div>
          <InsightBody query={query} />
        </CardBody>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Faixa recolhida — tem que valer a leitura sozinha
// ----------------------------------------------------------------------------

/**
 * O texto de uma linha é o que a pessoa vê 99% do tempo. Se ele disser só
 * "insights disponíveis", o card fechado não informa nada e ninguém abre.
 * Então ele diz o VEREDITO: quantos pontos pedem atenção, ou que não há
 * nenhum.
 */
function FaixaResumo({
  query,
  resumo,
}: {
  query: ReturnType<typeof useEquipmentInsight>;
  resumo: Resumo | null;
}) {
  if (query.isLoading) return <>Analisando padrões…</>;
  if (query.isError || !resumo) return <>Análise ainda não disponível pra este ativo</>;

  const partes: string[] = [];
  if (resumo.criticos) partes.push(`${resumo.criticos} crítico${resumo.criticos > 1 ? "s" : ""}`);
  if (resumo.atencoes) partes.push(`${resumo.atencoes} ponto${resumo.atencoes > 1 ? "s" : ""} de atenção`);

  if (partes.length === 0) {
    return <>Sem desvios relevantes nos últimos 7 dias</>;
  }
  return (
    <span
      className={clsx(
        "font-medium",
        resumo.criticos
          ? "text-red-600 dark:text-red-400"
          : "text-amber-600 dark:text-amber-400",
      )}
    >
      {partes.join(" · ")}
    </span>
  );
}

function SeloScore({ resumo }: { resumo: Resumo }) {
  const palette = GRADE_COLOR[resumo.grade];
  return (
    <span
      className={clsx(
        "hidden flex-none items-center gap-1.5 rounded-full bg-surface-50 px-2.5 py-1 ring-1 ring-surface-200/70 sm:inline-flex dark:bg-slate-800/60 dark:ring-slate-700/60",
        palette.text,
      )}
      title="Health score deste ativo"
    >
      <span className="text-sm font-bold tabular-nums">{resumo.score}</span>
      <span className="text-[10px] uppercase tracking-wide opacity-80">{palette.label}</span>
    </span>
  );
}

// ----------------------------------------------------------------------------
// Body — varia por estado
// ----------------------------------------------------------------------------

function InsightBody({ query }: { query: ReturnType<typeof useEquipmentInsight> }) {
  if (query.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[200px,1fr]">
        <div className="h-40 rounded-xl bg-surface-100 dark:bg-slate-800/40 animate-pulse" />
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 rounded-lg bg-surface-100 dark:bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-surface-200 dark:border-slate-700 bg-surface-50/50 dark:bg-slate-900/50 p-4">
        <Lightbulb className="mt-0.5 h-5 w-5 flex-none text-violet-500" />
        <div className="text-sm text-slate-600 dark:text-slate-400">
          <p className="font-medium text-slate-700 dark:text-slate-300">
            Análise inteligente ainda não disponível pra este ativo.
          </p>
          <p className="mt-1">
            O envpulse AI processa todos os equipamentos a cada 6 horas. Em
            ativos recém-cadastrados, aguarde o próximo ciclo. Você também
            pode clicar em <span className="font-semibold">Atualizar</span>
            {" "}pra forçar a busca.
          </p>
        </div>
      </div>
    );
  }

  const data = query.data;
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-[200px,1fr]">
      {/* Health Score à esquerda */}
      <HealthScoreRing
        score={data.health_score}
        grade={data.health_grade}
        breakdown={(data.features?.health_breakdown ?? {}) as Breakdown}
      />

      {/* Findings à direita */}
      <FindingsList findings={data.findings.filter(ehFinding)} />

      {/* Curiosidades ocupam a largura toda, embaixo dos dois */}
      <Curiosidades itens={data.findings.filter((f) => !ehFinding(f))} />
    </div>
  );
}

/** Sem `tone` gravado = finding. Insights antigos precedem o campo. */
function ehFinding(f: import("@/types").InsightFinding): boolean {
  return (f.tone ?? "finding") === "finding";
}

// ----------------------------------------------------------------------------
// Curiosidades — perguntas cuja resposta já está pronta
// ----------------------------------------------------------------------------

/**
 * O que separa isto dos findings não é a importância, é a POSTURA.
 *
 * Finding afirma: "a tomada desarmou 3 vezes". Ocupa espaço porque pede
 * ação. Curiosidade convida: "como é o degelo deste ativo?" — e a
 * resposta já está calculada, então abrir custa zero: nada de spinner,
 * nada de ida ao servidor, nada de modelo. Sai de
 * `equipment_insights.features`, que o worker materializou.
 *
 * Recolhidas por default de propósito: informação boa exibida o tempo todo
 * vira papel de parede, e aí some junto com o resto quando algo importa
 * de verdade.
 */
function Curiosidades({ itens }: { itens: import("@/types").InsightFinding[] }) {
  const [aberta, setAberta] = useState<string | null>(null);

  if (itens.length === 0) return null;

  return (
    <div className="md:col-span-2 border-t border-surface-100 dark:border-slate-800 pt-4">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
        <Sparkles className="h-3 w-3" />
        Sobre este ativo
      </div>

      <div className="flex flex-wrap gap-2">
        {itens.map((f) => {
          const ativa = aberta === f.type;
          return (
            <button
              key={f.type}
              type="button"
              onClick={() => setAberta(ativa ? null : f.type)}
              aria-expanded={ativa}
              className={clsx(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
                ativa
                  ? "border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200"
                  : "border-surface-200 text-slate-600 hover:bg-surface-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800",
              )}
            >
              {f.title}
              <ChevronDown
                className={clsx(
                  "h-3 w-3 transition-transform",
                  ativa && "rotate-180",
                )}
              />
            </button>
          );
        })}
      </div>

      {itens.map((f) =>
        aberta === f.type ? (
          <p
            key={f.type}
            className="mt-3 rounded-lg bg-surface-50 px-3 py-2.5 text-xs leading-relaxed text-slate-700 ring-1 ring-surface-200/60 dark:bg-slate-900/50 dark:text-slate-300 dark:ring-slate-700/60"
          >
            {f.description}
          </p>
        ) : null,
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Health Score ring + breakdown
// ----------------------------------------------------------------------------

type Breakdown = Record<
  string,
  { score: number; weight: number; reason?: string; informativo?: boolean }
>;

function HealthScoreRing({
  score,
  grade,
  breakdown,
}: {
  score: number;
  grade: HealthGrade;
  breakdown: Breakdown;
}) {
  const palette = GRADE_COLOR[grade];

  // SVG ring
  const r = 56;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;

  // Ordem das dimensões pra exibir. Aberturas aparecem com peso 0: foram
  // medidas e NÃO entram na nota — abrir a porta é usar o equipamento, não
  // sinal de que ele piorou. Esconder daria a impressão de que ninguém olhou.
  const dimensions: { key: string; label: string }[] = [
    { key: "temperature", label: "Temperatura" },
    { key: "consumption", label: "Consumo" },
    { key: "door",        label: "Aberturas" },
  ];

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle
            cx="70" cy="70" r={r}
            fill="none" strokeWidth="10"
            className="text-surface-200 dark:text-slate-700"
            stroke="currentColor"
          />
          <circle
            cx="70" cy="70" r={r}
            fill="none" strokeWidth="10" strokeLinecap="round"
            className={palette.ring}
            stroke="currentColor"
            strokeDasharray={c}
            strokeDashoffset={offset}
            transform="rotate(-90 70 70)"
            style={{ transition: "stroke-dashoffset 0.6s ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className={clsx("text-3xl font-bold tabular-nums", palette.text)}>
            {score}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            health score
          </div>
        </div>
      </div>
      <div className={clsx("text-sm font-semibold", palette.text)}>{palette.label}</div>

      {/* Breakdown por dimensão */}
      <div className="mt-2 w-full space-y-1.5 text-xs">
        {dimensions.map((dim) => {
          const item = breakdown[dim.key];
          if (!item) return null;
          return (
            <div key={dim.key} className="flex items-center justify-between gap-2">
              <span className="text-slate-500 dark:text-slate-400">{dim.label}</span>
              <span className="flex items-center gap-1.5">
                <span
                  className={clsx(
                    "tabular-nums",
                    item.informativo
                      ? "text-slate-400 dark:text-slate-500"
                      : "text-slate-700 dark:text-slate-300",
                  )}
                >
                  {item.score}
                </span>
                <span className="text-[10px] text-slate-400 dark:text-slate-500">
                  {item.informativo ? "operação" : `${item.weight}%`}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Lista de findings
// ----------------------------------------------------------------------------

function FindingsList({ findings }: { findings: import("@/types").InsightFinding[] }) {
  // Ordena por severity (já vem ordenado do backend mas garante)
  const sorted = useMemo(() => {
    const order: Record<InsightSeverity, number> = { critical: 0, warning: 1, info: 2 };
    return [...findings].sort((a, b) => order[a.severity] - order[b.severity]);
  }, [findings]);

  if (sorted.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-950/30 p-4">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-emerald-600 dark:text-emerald-400" />
        <div className="text-sm">
          <p className="font-medium text-emerald-900 dark:text-emerald-200">
            Tudo nos eixos.
          </p>
          <p className="mt-1 text-emerald-800 dark:text-emerald-300">
            Não identifiquei desvios significativos vs a baseline deste ativo
            nos últimos 7 dias.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {sorted.map((f, i) => (
        <FindingCard key={i} finding={f} />
      ))}
    </div>
  );
}

function FindingCard({ finding }: { finding: import("@/types").InsightFinding }) {
  const style = SEVERITY_STYLE[finding.severity];
  const Icon = style.icon;
  const DimIcon = findingDimensionIcon(finding.type);

  return (
    <div className={clsx("rounded-lg ring-1 p-3", style.bg, style.ring)}>
      <div className="flex items-start gap-3">
        <div className={clsx("mt-0.5 flex-none", style.iconClr)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <DimIcon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {finding.title}
            </h4>
          </div>
          <p className="mt-1 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
            {finding.description}
          </p>
          {finding.suggestion && (
            <div className="mt-2 inline-flex items-start gap-1.5 rounded-md bg-white/60 dark:bg-slate-900/40 px-2 py-1 text-[11px] text-slate-700 dark:text-slate-300 ring-1 ring-surface-200/60 dark:ring-slate-700/60">
              <Lightbulb className="mt-0.5 h-3 w-3 flex-none text-amber-500" />
              <span>
                <span className="font-medium">Sugestão:</span> {finding.suggestion}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
